<template>
  <div class="mb-3 lg:mb-10">
    <Breadcrumbs v-if="breadcrumbs" :links="breadcrumbs" />
    <template v-if="preListingContent && isFirstPage && !productsFetching">
      <component
        :is="preContent.component"
        v-for="preContent in preListingContent"
        :key="preContent._uid"
        :blok="preContent"
        class="mb-4.5 mt-4"
      />
    </template>

    <div class="flex flex-col items-center lg:flex-row lg:items-baseline">
      <div
        class="mb-4 mr-0 whitespace-nowrap text-lg font-medium leading-none lg:mb-0 lg:mr-2 lg:text-2xl lg:font-bold"
      >
        {{ heading }}
      </div>
      <HorizontalScrollNavigation
        v-if="horizontalSideNavigationCategories?.length"
        class="mb-3 block lg:hidden"
        :categories="horizontalSideNavigationCategories"
        :fetching="fetching"
      />
      <div
        class="flex w-full justify-between"
        :class="{ 'flex-col-reverse gap-2': isMobile }"
      >
        <div
          v-if="resultsCount && !fetching"
          class="flex items-center gap-1 text-sm font-normal leading-none text-gray-secondary lg:font-bold"
        >
          <span>{{ resultsCount }}</span>
          <span>{{ $t('wishlist.products_count', resultsCount) }}</span>
        </div>

        <div
          v-if="
            !fetching &&
            products &&
            (!showNewDesktopPlpQuickFilters || isMobileOrTablet)
          "
          class="flex w-full justify-end"
        >
          <div
            class="order-1 flex items-center text-sm"
            :class="{
              'w-full flex-col gap-2 space-x-0': isMobile,
              'space-x-4': !isMobile,
            }"
          >
            <div class="hidden sm:flex">
              <SortingMenu :values="plpSortingValues" />
            </div>
            <Button
              data-test-id="filter-toggle-button"
              type="tertiary"
              :is-full-width="isMobile"
              :aria-label="$t('filter.filters_aria_label')"
              :aria-expanded="filtersOpen"
              @click="toggleFilter"
            >
              <template #icon>
                <IconFilter class="size-5" />
              </template>
              <span class="sm:hidden">
                {{ $t('plp.filter_and_sort') }}
              </span>
              <span class="hidden sm:block">
                {{ $t('plp.filter') }}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!showNewDesktopPlpQuickFilters || isMobileOrTablet"
      class="flex max-w-full flex-col"
    >
      <ActiveFilters
        v-if="activeFilters && filters"
        :filters="filters"
        :active-filters="activeFilters"
        @reset-price-filter="$emit('resetPriceFilter')"
        @apply-attribute-filter="applyAttributeFilter"
        @reset-filter="$emit('resetFilter', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import type { Category, Product } from '@scayle/storefront-nuxt'
import useSlideIn from '../../../../composables/useSlideIn'
import usePlpBreadcrumbs from '../../../../composables/plp/usePlpBreadcrumbs'
import usePlpQueryFilterState from '../../../../composables/plp/usePlpQueryFilterState'
import type { SbListingPage } from '~/cms/storyblok/types/storyblok'
import useExperiments from '~/composables/useExperiments'
import { useDeviceType } from '~/composables/useDeviceType'

interface PLPHeaderProps {
  heading: string
  resultsCount: number | null
  fetching: boolean
  products: Product[] | undefined
  productsFetching: boolean
  preListingContent: SbListingPage['pre_listing_content']
  postListingContent: SbListingPage['post_listing_content']
  isFirstPage: boolean
  filters: any
  plpSortingValues: any[]
  selectedCategory: Category
  categories: Category[]
}

const props = defineProps<PLPHeaderProps>()
const emit = defineEmits<{
  (e: 'applyAttributeFilter', slug: string, values: number[]): void
  (e: 'resetFilter', slug: string): void
  (e: 'resetPriceFilter'): void
}>()
const {
  heading,
  resultsCount,
  fetching,
  products,
  productsFetching,
  preListingContent,
  isFirstPage,
  filters,
  plpSortingValues,
  selectedCategory,
  categories,
} = toRefs(props)

const { activeFilters } = usePlpQueryFilterState()
const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobile, isMobileOrTablet } = useDeviceType()

const { breadcrumbs } = usePlpBreadcrumbs({
  selectedCategory,
  categories,
})

const horizontalSideNavigationCategories = computed(
  () => selectedCategory.value?.children,
)

const { isOpen: filtersOpen, toggle: toggleFilter } =
  useSlideIn('FilterSlideIn')

const applyAttributeFilter = async (slug: string, values: number[]) => {
  emit('applyAttributeFilter', slug, values)
}
</script>
